/**
 * 통합 아바타 캐싱 시스템
 * - 원본 해상도 우선 캐싱
 * - Service Worker 스타일 캐싱
 * - 점진적 로딩 지원
 */

import { getPublicAvatarUrl } from './publicAvatarUtils';

// 원본 해상도 설정
const ORIGINAL_SIZE = 512; // 원본 해상도
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24시간
const CACHE_KEY_PREFIX = 'avatar_cache_';

class AvatarCacheSystem {
  constructor() {
    this.memoryCache = new Map();
    this.loadingPromises = new Map(); // 중복 요청 방지
    this.preloadQueue = new Set();
    this.isPreloading = false;
    
    this.loadFromStorage();
    this.startPreloadWorker();
  }

  // localStorage에서 캐시 로드
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('avatar_cache_system');
      if (stored) {
        const data = JSON.parse(stored);
        const now = Date.now();
        
        // TTL 검사하며 유효한 캐시만 로드
        Object.entries(data).forEach(([key, item]) => {
          if (item.timestamp && (now - item.timestamp < CACHE_TTL)) {
            this.memoryCache.set(key, item);
          }
        });
        
        console.log(`🎯 Avatar cache loaded: ${this.memoryCache.size} items`);
      }
    } catch (error) {
      console.error('Failed to load avatar cache:', error);
    }
  }

  // localStorage에 캐시 저장
  saveToStorage() {
    try {
      const cacheData = {};
      this.memoryCache.forEach((value, key) => {
        cacheData[key] = value;
      });
      
      localStorage.setItem('avatar_cache_system', JSON.stringify(cacheData));
    } catch (error) {
      console.error('Failed to save avatar cache:', error);
      // localStorage 용량 초과 시 오래된 캐시 정리
      this.cleanupOldCache();
    }
  }

  // 오래된 캐시 정리
  cleanupOldCache() {
    const now = Date.now();
    let cleaned = 0;
    
    this.memoryCache.forEach((item, key) => {
      if (!item.timestamp || (now - item.timestamp > CACHE_TTL)) {
        this.memoryCache.delete(key);
        cleaned++;
      }
    });
    
    console.log(`🧹 Cleaned ${cleaned} old avatar cache entries`);
    this.saveToStorage();
  }

  // 원본 해상도 이미지 URL 생성
  getOriginalAvatarUrl(userID) {
    return getPublicAvatarUrl(userID, ORIGINAL_SIZE);
  }

  // 크기별 이미지 URL 생성 (원본에서 클라이언트 리사이징)
  getResizedAvatarUrl(userID, size) {
    return getPublicAvatarUrl(userID, size);
  }

  // 캐시 키 생성
  getCacheKey(userID, type = 'original') {
    return `${CACHE_KEY_PREFIX}${userID}_${type}`;
  }

  // 이미지 preload 및 캐싱
  async preloadAvatar(userID, priority = 'normal') {
    const cacheKey = this.getCacheKey(userID, 'original');
    
    // 이미 캐시된 경우 스킵
    if (this.memoryCache.has(cacheKey)) {
      return this.memoryCache.get(cacheKey).url;
    }

    // 이미 로딩 중인 경우 대기
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    const promise = this.loadAndCacheAvatar(userID);
    this.loadingPromises.set(cacheKey, promise);
    
    try {
      const result = await promise;
      this.loadingPromises.delete(cacheKey);
      return result;
    } catch (error) {
      this.loadingPromises.delete(cacheKey);
      throw error;
    }
  }

  // 실제 이미지 로드 및 캐싱
  async loadAndCacheAvatar(userID) {
    const originalUrl = this.getOriginalAvatarUrl(userID);
    const cacheKey = this.getCacheKey(userID, 'original');

    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        // 원본 이미지 캐싱
        const cacheItem = {
          url: originalUrl,
          userID,
          size: ORIGINAL_SIZE,
          timestamp: Date.now(),
          loaded: true
        };
        
        this.memoryCache.set(cacheKey, cacheItem);
        
        // 여러 크기로 파생 캐싱 (옵션)
        this.cacheMultipleSizes(userID, img);
        
        this.saveToStorage();
        console.log(`✅ Cached original avatar: ${userID}`);
        resolve(originalUrl);
      };

      img.onerror = () => {
        console.error(`❌ Failed to load avatar: ${userID}`);
        reject(new Error(`Failed to load avatar: ${userID}`));
      };

      // CORS 설정
      img.crossOrigin = 'anonymous';
      img.src = originalUrl;
    });
  }

  // 여러 크기로 파생 캐싱 (선택적)
  cacheMultipleSizes(userID, originalImg) {
    const commonSizes = [32, 48, 64, 80, 160, 256];
    
    commonSizes.forEach(size => {
      const cacheKey = this.getCacheKey(userID, `size_${size}`);
      if (!this.memoryCache.has(cacheKey)) {
        this.memoryCache.set(cacheKey, {
          url: this.getResizedAvatarUrl(userID, size),
          userID,
          size,
          timestamp: Date.now(),
          loaded: true,
          derivedFrom: 'original'
        });
      }
    });
  }

  // 백그라운드 preload 작업자
  startPreloadWorker() {
    setInterval(() => {
      if (!this.isPreloading && this.preloadQueue.size > 0) {
        this.processPreloadQueue();
      }
    }, 1000);
  }

  // preload 대기열 처리
  async processPreloadQueue() {
    if (this.isPreloading || this.preloadQueue.size === 0) return;
    
    this.isPreloading = true;
    const batch = Array.from(this.preloadQueue).slice(0, 5); // 한 번에 5개씩
    
    console.log(`🚀 Processing avatar preload batch: ${batch.length} items`);
    
    const promises = batch.map(async (userID) => {
      try {
        await this.preloadAvatar(userID);
        this.preloadQueue.delete(userID);
      } catch (error) {
        console.error(`Failed to preload avatar ${userID}:`, error);
        this.preloadQueue.delete(userID); // 실패해도 큐에서 제거
      }
    });

    await Promise.allSettled(promises);
    this.isPreloading = false;
  }

  // 대량 preload 예약
  batchPreload(userIDs) {
    const newUserIDs = userIDs.filter(id => {
      const cacheKey = this.getCacheKey(id, 'original');
      return !this.memoryCache.has(cacheKey) && !this.preloadQueue.has(id);
    });

    newUserIDs.forEach(id => this.preloadQueue.add(id));
    console.log(`📝 Added ${newUserIDs.length} avatars to preload queue (total: ${this.preloadQueue.size})`);
    
    return newUserIDs.length;
  }

  // 캐시된 아바타 URL 가져오기
  getCachedAvatarUrl(userID, size = ORIGINAL_SIZE) {
    // 원본 우선 확인
    const originalKey = this.getCacheKey(userID, 'original');
    const originalCache = this.memoryCache.get(originalKey);
    
    if (originalCache && originalCache.loaded) {
      // 원본이 있으면 크기별 URL 생성 (Firebase Storage가 리사이징)
      return size === ORIGINAL_SIZE ? originalCache.url : this.getResizedAvatarUrl(userID, size);
    }

    // 크기별 캐시 확인
    const sizeKey = this.getCacheKey(userID, `size_${size}`);
    const sizeCache = this.memoryCache.get(sizeKey);
    
    if (sizeCache && sizeCache.loaded) {
      return sizeCache.url;
    }

    return null; // 캐시 없음
  }

  // 캐시 상태 확인
  isCached(userID) {
    const originalKey = this.getCacheKey(userID, 'original');
    return this.memoryCache.has(originalKey);
  }

  // 캐시 통계
  getCacheStats() {
    const totalCached = this.memoryCache.size;
    const queuedForPreload = this.preloadQueue.size;
    const originalCached = Array.from(this.memoryCache.keys())
      .filter(key => key.includes('_original')).length;
    
    return {
      totalCached,
      originalCached,
      queuedForPreload,
      isPreloading: this.isPreloading
    };
  }

  // 캐시 초기화
  clearCache() {
    this.memoryCache.clear();
    this.preloadQueue.clear();
    this.loadingPromises.clear();
    
    try {
      localStorage.removeItem('avatar_cache_system');
      console.log('🗑️ Avatar cache cleared');
    } catch (error) {
      console.error('Failed to clear avatar cache:', error);
    }
  }
}

// 전역 인스턴스
const avatarCacheSystem = new AvatarCacheSystem();

export default avatarCacheSystem;

// 편의 함수들
export const preloadAvatars = (userIDs) => avatarCacheSystem.batchPreload(userIDs);
export const getCachedAvatarUrl = (userID, size) => avatarCacheSystem.getCachedAvatarUrl(userID, size);
export const isAvatarCached = (userID) => avatarCacheSystem.isCached(userID);
export const getAvatarCacheStats = () => avatarCacheSystem.getCacheStats();
export const clearAvatarCache = () => avatarCacheSystem.clearCache();